/**
 * TimeSystem — client-side day/night cycle tracker (DON-599).
 *
 * The server owns the authoritative `dayTimeMs` clock and broadcasts it via
 * Colyseus room state. TimeSystem receives that snapshot, extrapolates
 * locally each frame using delta time, and fires Phaser events on phase
 * transitions (sunrise, sunset, dawn, dusk).
 *
 * All animation is frame-rate independent; never use frame counts (§1.8).
 *
 * Usage:
 *   // In scene create():
 *   this.timeSystem = new TimeSystem(this);
 *   this.timeSystem.syncFromServer({ dayTimeMs, serverTimestampMs });
 *
 *   // In scene update(time, delta):
 *   this.timeSystem.update(delta);
 *
 *   // In scene shutdown():
 *   this.timeSystem.destroy();
 *
 * Events emitted on scene.events:
 *   TimeSystem.EVENTS.PHASE_CHANGE  → { from: TimeOfDay, to: TimeOfDay }
 *   TimeSystem.EVENTS.SUNRISE       → { fraction: number }
 *   TimeSystem.EVENTS.SUNSET        → { fraction: number }
 *   TimeSystem.EVENTS.TICK          → { dayTimeMs, fraction, phase, ambient }
 */

import * as Phaser from 'phaser';
import {
  DAY_DURATION_MS,
  TOD_AMBIENT,
  type AmbientLight,
  type TimeOfDay,
  type WorldTimeState,
  getDayFraction,
  getTimeOfDay,
  getPhaseProgress,
  lerpAmbient,
  TOD_BOUNDARIES,
  LUNAR_DAYS_PER_CYCLE,
  LUNAR_CYCLE_MS,
  FULL_MOON_PHASE,
  getMoonPhase,
  isFullMoon,
} from '@game/shared';

/** How often (ms) to emit a TICK event even without a phase change. */
const TICK_INTERVAL_MS = 1000;

export interface TimeTickPayload {
  dayTimeMs: number;
  fraction: number;
  phase: TimeOfDay;
  ambient: AmbientLight;
  /** Shadow angle in degrees (0=right, clockwise). */
  shadowAngle: number;
  /** Shadow length multiplier (0..1). */
  shadowLength: number;
}

/** Next-phase lookup for transition event names. */
const NEXT_PHASE: Record<TimeOfDay, TimeOfDay> = {
  dawn:  'day',
  day:   'dusk',
  dusk:  'night',
  night: 'dawn',
};

export class TimeSystem {
  static readonly EVENTS = {
    PHASE_CHANGE: 'tod:phaseChange',
    SUNRISE:      'tod:sunrise',
    SUNSET:       'tod:sunset',
    TICK:         'tod:tick',
  } as const;

  private scene: Phaser.Scene;
  private dayTimeMs: number = 0;
  private currentPhase: TimeOfDay = 'day';
  private sinceLastTick: number = 0;

  /** Hooks for external systems (NPC schedules, festivals, lunar cycle). */
  private readonly hooks = new Map<string, Set<(payload: TimeTickPayload) => void>>();

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  // ── Server sync ────────────────────────────────────────────────────────────

  /**
   * Synchronise from a Colyseus state snapshot. Call this whenever the server
   * broadcasts a time update (e.g. in `onStateChange`). Compensates for
   * network latency using `serverTimestampMs`.
   */
  syncFromServer(state: WorldTimeState): void {
    const latencyMs = Date.now() - state.serverTimestampMs;
    // Clamp to 0 to avoid negative compensation on misconfigured clocks.
    const compensated = state.dayTimeMs + Math.max(0, latencyMs);
    this.dayTimeMs = compensated % DAY_DURATION_MS;
    this.currentPhase = getTimeOfDay(getDayFraction(this.dayTimeMs));
  }

  // ── Per-frame update ────────────────────────────────────────────────────────

  /**
   * Must be called from the scene's `update(time, delta)`. `delta` is Phaser's
   * ms-since-last-frame — never a fixed step.
   */
  update(delta: number): void {
    const prevPhase = this.currentPhase;
    this.dayTimeMs = (this.dayTimeMs + delta) % DAY_DURATION_MS;

    const fraction = getDayFraction(this.dayTimeMs);
    const newPhase = getTimeOfDay(fraction);

    if (newPhase !== prevPhase) {
      this.currentPhase = newPhase;
      this.scene.events.emit(TimeSystem.EVENTS.PHASE_CHANGE, {
        from: prevPhase,
        to:   newPhase,
      });

      // Sunrise fires when transitioning night → dawn
      if (prevPhase === 'night' && newPhase === 'dawn') {
        this.scene.events.emit(TimeSystem.EVENTS.SUNRISE, { fraction });
      }
      // Sunset fires when transitioning dusk → night
      if (prevPhase === 'dusk' && newPhase === 'night') {
        this.scene.events.emit(TimeSystem.EVENTS.SUNSET, { fraction });
      }
    }

    this.sinceLastTick += delta;
    if (this.sinceLastTick >= TICK_INTERVAL_MS) {
      this.sinceLastTick -= TICK_INTERVAL_MS;
      const payload = this.buildPayload(fraction, newPhase);
      this.scene.events.emit(TimeSystem.EVENTS.TICK, payload);
      this.dispatchHooks(payload);
    }
  }

  // ── Queries ────────────────────────────────────────────────────────────────

  getPhase(): TimeOfDay {
    return this.currentPhase;
  }

  getDayTimeMs(): number {
    return this.dayTimeMs;
  }

  getFraction(): number {
    return getDayFraction(this.dayTimeMs);
  }

  /**
   * Returns interpolated ambient light between the current phase and the next,
   * blended by how far through the current phase we are.
   */
  getAmbientLight(): AmbientLight {
    const fraction  = this.getFraction();
    const phase     = this.currentPhase;
    const nextPhase = NEXT_PHASE[phase];
    const progress  = getPhaseProgress(fraction);
    return lerpAmbient(TOD_AMBIENT[phase], TOD_AMBIENT[nextPhase], progress);
  }

  /**
   * Shadow angle in degrees (0=east, clockwise). Completes a full 360° per
   * day, reaching 270° (pointing west) at high noon.
   */
  getShadowAngle(): number {
    return (this.getFraction() * 360) % 360;
  }

  /**
   * Shadow length multiplier (0..1). Longest at dawn/dusk, shortest at noon,
   * zero during full night.
   */
  getShadowLength(): number {
    const f = this.getFraction();
    const { DAY_START, DUSK_START, NIGHT_START } = TOD_BOUNDARIES;
    if (f < DAY_START)   return 1.0;  // dawn — long shadows
    if (f < DUSK_START) {
      // noon at 40% of day fraction → sin curve for shadow length
      const dayFrac = (f - DAY_START) / (DUSK_START - DAY_START);
      return 0.2 + 0.8 * Math.abs(Math.sin(dayFrac * Math.PI));
    }
    if (f < NIGHT_START) return 1.0;  // dusk — long shadows
    return 0.0;                        // night — no ground shadows
  }

  // ── Lunar cycle (B-027) ────────────────────────────────────────────────────
  //
  // The lunar period is defined in `@game/shared/types/time.ts`:
  //   LUNAR_DAYS_PER_CYCLE = 28 in-world days
  //   LUNAR_CYCLE_MS       = 28 × DAY_DURATION_MS  (≈ 9 h 20 min real)
  //   FULL_MOON_PHASE      = 0.5
  //
  // GDD §7.5 specifies "1 % per in-world full moon" but does NOT pin a lunar
  // period; 28 days is the canonical project-wide default and may be tuned
  // by a future balance pass. The constants are re-exported here as static
  // members so renderers (sky shader, moon icon, ritual gates) can read the
  // same source of truth without re-importing from `@game/shared`.
  //
  // Phase basis: lunar phase is derived from **wall-clock ms** (`Date.now()`)
  // rather than the wrapping `dayTimeMs` clock. The server's authoritative
  // dimming-star tick also uses wall-clock ms (see `DimmingService`), so
  // client + server agree on which moment is "full moon" without an extra
  // sync field on the wire.

  static readonly LUNAR_DAYS_PER_CYCLE = LUNAR_DAYS_PER_CYCLE;
  static readonly LUNAR_CYCLE_MS = LUNAR_CYCLE_MS;
  static readonly FULL_MOON_PHASE = FULL_MOON_PHASE;

  /**
   * Returns the current moon phase in [0, 1).
   *   0.0  → new moon
   *   0.25 → first quarter
   *   0.5  → full moon
   *   0.75 → last quarter
   *
   * Default basis is `Date.now()`; tests may pass a deterministic timestamp.
   */
  getMoonPhase(now: number = Date.now()): number {
    return getMoonPhase(now);
  }

  /**
   * True if `now` falls within the one-in-world-day "full moon" window
   * centred on `FULL_MOON_PHASE`. Useful for triggering full-moon rituals,
   * cosmetic moon glow, or ambient SFX cues.
   */
  isFullMoon(now: number = Date.now()): boolean {
    return isFullMoon(now);
  }

  // ── Extension hooks ────────────────────────────────────────────────────────

  /**
   * Register an external hook (NPC schedules, seasonal festivals, lunar cycle).
   * `hookId` must be unique per caller; hooks fire on every TICK.
   */
  addHook(hookId: string, cb: (payload: TimeTickPayload) => void): void {
    if (!this.hooks.has(hookId)) {
      this.hooks.set(hookId, new Set());
    }
    this.hooks.get(hookId)!.add(cb);
  }

  removeHook(hookId: string): void {
    this.hooks.delete(hookId);
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  destroy(): void {
    this.hooks.clear();
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  private buildPayload(fraction: number, phase: TimeOfDay): TimeTickPayload {
    return {
      dayTimeMs:    this.dayTimeMs,
      fraction,
      phase,
      ambient:      this.getAmbientLight(),
      shadowAngle:  this.getShadowAngle(),
      shadowLength: this.getShadowLength(),
    };
  }

  private dispatchHooks(payload: TimeTickPayload): void {
    for (const callbacks of this.hooks.values()) {
      for (const cb of callbacks) {
        cb(payload);
      }
    }
  }
}
